/**
 * PRP-WEB-2 W2.1-W2.4: the plan page's pointer, menu and keyboard wiring. Feeds the viewport's
 * presses (plan feet) to the `EditController`, previews each drag frame without touching
 * history, and commits the release as one undo step through `useEditPlan`. Also the split
 * tool, the right-click menus, double-click editing and the editor's keys.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  bbox, buildWallGraph, clone, deleteOpening, deleteRoom, doSplit, dragOutRoom, duplicateRoom, edgeOf, findRoom, labelPt,
  snapIn, tidy, wallLabels, type Plan, type Pt, type Room,
} from '@sketch-core';
import {
  EditController, grabAt, handlePt, hitAt, selOf, type DragFrame, type Grab, type Sel,
} from '../controller/editController';
import { keyIntent } from '../controller/keys';
import { menuFor, menuTitle, type MenuItem, type MenuTarget, type PromptSpec } from '../menus/menuModel';
import type { EditorPointer, ViewportEditor } from '../PlanViewport';
import { planMetrics, type ViewBox } from '../viewBox';
import type { UnderlayApi } from '../underlay/useUnderlay';
import type { EditPlan } from './useEditPlan';

export const newRoomId = (): string =>
  (globalThis.crypto?.randomUUID?.() ?? `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

/** The split tool's line across room `rid` through the point `off` along wall `vid`. */
export function splitPreview(plan: Plan, rid: string, vid: number, off: number): [Pt, Pt] | null {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return null;
  const horiz = Math.abs(E.a.y - E.b.y) < 1e-6, vert = Math.abs(E.a.x - E.b.x) < 1e-6;
  if (!horiz && !vert) return null;
  const [x0, y0, x1, y1] = bbox(r.corners);
  const L = Math.hypot(E.b.x - E.a.x, E.b.y - E.a.y) || 1;
  const p = { x: E.a.x + ((E.b.x - E.a.x) * off) / L, y: E.a.y + ((E.b.y - E.a.y) * off) / L };
  return horiz ? [{ x: snapIn(p.x), y: y0 }, { x: snapIn(p.x), y: y1 }] : [{ x: x0, y: snapIn(p.y) }, { x: x1, y: snapIn(p.y) }];
}

export function useCanvasEditor(core: EditPlan, opts: { view: ViewBox | null; fit: ViewBox | null; setView: (v: ViewBox) => void; underlay: UnderlayApi; chamfer: ((rid: string, vid: number, end: 'start' | 'end') => string | null) | null }) {
  const { plan, sel, setSel, groups } = core;
  const ctl = useRef(new EditController());
  const grab = useRef<Grab | null>(null);
  const before = useRef<Plan | null>(null);
  const underlayDrag = useRef(false);
  /** The first click of a double-click selects, which can open the panel and shift the plan under the
   *  pointer; the double-click acts where that first click landed on the plan. */
  const taps = useRef<{ wp: Pt; t: number }[]>([]);
  const [frame, setFrame] = useState<DragFrame | null>(null);
  const [hover, setHover] = useState<Sel | null>(null);
  const [tool, setTool] = useState<'split' | null>(null);
  const [splitLine, setSplitLine] = useState<[Pt, Pt] | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; target: MenuTarget } | null>(null);
  const [prompt, setPrompt] = useState<PromptSpec | null>(null);
  const [clip, setClip] = useState<Room | null>(null);
  const U = opts.underlay;

  const say = core.setNote;

  /** A tool or picture mode is on (split, set scale, move picture, a drag-out or picture drag). */
  const modeOn = (): boolean => !!tool || U.mode !== 'none' || underlayDrag.current || ctl.current.active;

  // Review W6: entering a mode drops the selection, so a later Delete cannot act on a room the
  // user picked before the mode (and can no longer see as the target).
  useEffect(() => {
    if (tool || U.mode !== 'none') setSel(null);
  }, [tool, U.mode, setSel]);

  const paste = useCallback((at: Pt) => {
    if (!clip) return;
    const id = newRoomId();
    core.act((p) => {
      const tmp = clone(clip);
      tmp.id = '__clip';
      p.rooms.push(tmp);
      const res = duplicateRoom(p, '__clip', { id });
      p.rooms = p.rooms.filter((r) => r.id !== '__clip');
      if (!res.ok) return res;
      const r = findRoom(p, id)!;
      const [x0, y0, x1, y1] = bbox(r.corners);
      const dx = snapIn(at.x - (x0 + x1) / 2), dy = snapIn(at.y - (y0 + y1) / 2);
      r.corners.forEach((c) => { c.x += dx; c.y += dy; });
      tidy(p);
      return { ok: true };
    }, { select: { type: 'room', rid: id } });
  }, [clip, core]);

  const menuCtx = useMemo(() => (plan ? {
    plan, groups, editable: core.editable,
    act: (a: Parameters<typeof core.act>[0], after?: { select?: Sel }) => core.act(a, after),
    setGroups: core.changeGroups,
    prompt: setPrompt,
    copy: (rid: string) => { const r = findRoom(plan, rid); if (r) { setClip(clone(r)); say(`Copied ${r.name}. Right-click empty space and choose Paste room, or press Ctrl+V.`); } },
    canPaste: !!clip,
    paste,
    zoomFit: () => { if (opts.fit) opts.setView(opts.fit); },
    newId: newRoomId,
    wallLength: (rid: string, vid: number, ft: number) => { const r = core.wallLength(rid, vid, ft); return r.ok ? null : r.message; },
    chamfer: opts.chamfer,
  } : null), [plan, groups, core, clip, paste, opts, say]);

  const menuItems: MenuItem[] = menu && menuCtx ? menuFor(menu.target, menuCtx) : [];

  const targetAt = (wp: Pt, p: EditorPointer): MenuTarget => {
    const h = plan ? hitAt(plan, wp, p.tolFt, sel) : null;
    if (h?.type === 'wall') return { kind: 'wall', rid: h.rid, vid: h.vid, off: h.off, wp };
    if (h?.type === 'open') { const s = selOf(plan!, h) as Extract<Sel, { type: 'open' }>; return { kind: 'open', rid: s.rid, oid: s.oid, wp }; }
    if (h?.type === 'room') return { kind: 'room', rid: h.rid, wp };
    return { kind: 'empty', wp };
  };

  const finish = () => {
    const e = ctl.current.end();
    setFrame(null);
    const b = before.current;
    before.current = null;
    if (e.kind === 'plan') {
      const m = core.commit(e.before, e.working, e.sel);
      if (!m && e.message) say(e.message);
    } else if (e.kind === 'dragout') {
      if (b) core.preview(b);
      const id = newRoomId();
      const m = core.act((p) => dragOutRoom(p, e.rid, e.vid, e.depthFt, { id }), { select: { type: 'room', rid: id } });
      if (!m) setPrompt({ kind: 'text', title: 'Name the new room', value: 'Room', apply: (v) => core.act((p) => renameRoom2(p, id, v)) ?? undefined });
    } else {
      if (b) core.preview(b);
      if (e.message) say(e.message);
    }
  };

  const editor: ViewportEditor | undefined = plan ? {
    press: (wp, p) => {
      if (U.mode !== 'none' && U.u) { underlayDrag.current = true; return true; }
      underlayDrag.current = false;
      if (tool) return false;
      grab.current = grabAt(plan, sel, wp, p);
      return !!grab.current;
    },
    dragStart: (wp0, wp, p) => {
      if (underlayDrag.current) { U.begin(wp0); U.move(wp); return; }
      const g = grab.current;
      if (!g) return;
      const res = ctl.current.start(plan, g, wp0, core.editable);
      if ('message' in res) { say(res.message); return; }
      before.current = plan;
      setSel(res.sel);
      setHover(null);
      const f = ctl.current.move(wp, { alt: p.alt, pxPerFt: p.pxPerFt }) ?? res;
      core.preview(f.plan);
      setFrame(f);
    },
    dragMove: (wp, p) => {
      if (underlayDrag.current) { U.move(wp); return; }
      const f = ctl.current.move(wp, { alt: p.alt, pxPerFt: p.pxPerFt });
      if (!f) return;
      core.preview(f.plan);
      setFrame(f);
      if (f.sel) setSel(f.sel);
    },
    dragEnd: () => {
      if (underlayDrag.current) { underlayDrag.current = false; U.end(); return; }
      if (ctl.current.active) finish();
    },
    cancel: () => cancelDrag(),
    tap: (wp, p) => {
      if (underlayDrag.current) { underlayDrag.current = false; return; }
      taps.current = [...taps.current.slice(-1), { wp, t: Date.now() }];
      const h = hitAt(plan, wp, p.tolFt, sel);
      if (tool === 'split') {
        if (h?.type !== 'wall') { say('Click a wall of the room you want to split.'); return; }
        const id = newRoomId();
        const m = core.act((q) => doSplit(q, h.rid, h.vid, h.off, { id }));
        if (!m) { setTool(null); setSplitLine(null); say('Split. Drag the new line to move it.'); }
        return;
      }
      setSel(selOf(plan, h));
    },
    hover: (wp, p) => {
      if (!wp) { setHover(null); setSplitLine(null); return; }
      const h = hitAt(plan, wp, p.tolFt, sel);
      const s = selOf(plan, h);
      setHover((cur) => (JSON.stringify(cur) === JSON.stringify(s) ? cur : s));
      if (tool === 'split') setSplitLine(h?.type === 'wall' ? splitPreview(plan, h.rid, h.vid, h.off) : null);
    },
    menu: (wp, client, p) => {
      if (ctl.current.active) return;
      const t = targetAt(wp, p);
      setSel(t.kind === 'empty' ? null : t.kind === 'open' ? { type: 'open', rid: t.rid, oid: t.oid }
        : t.kind === 'wall' ? { type: 'wall', rid: t.rid, vid: t.vid, off: t.off } : { type: 'room', rid: t.rid });
      setMenu({ x: client.x, y: client.y, target: t });
    },
    dblclick: (wp0) => {
      const [a, b] = taps.current;
      const wp = a && b && b.t - a.t < 600 && Date.now() - b.t < 600 ? a.wp : wp0;
      taps.current = [];
      const { fs } = planMetrics(plan);
      const g = buildWallGraph(plan);
      const k = fs * 0.9;
      const dim = wallLabels(plan, g).find((l) => Math.hypot(l.at.x + l.dir.x * k - wp.x, l.at.y + l.dir.y * k - wp.y) <= fs * 1.1);
      if (dim) {
        const E = edgeOf(findRoom(plan, dim.rid)!, dim.vid);
        setPrompt({
          kind: 'length', title: `Wall length (${findRoom(plan, dim.rid)?.name ?? 'room'})`,
          valueFt: E ? Math.hypot(E.b.x - E.a.x, E.b.y - E.a.y) : dim.valueFt,
          apply: (ft) => { const r = core.wallLength(dim.rid, dim.vid, ft); return r.ok ? undefined : r.message; },
        });
        return;
      }
      // Elsewhere in a room (its name, its floor): rename it, as a double tap opens room properties on the phone.
      const h = hitAt(plan, wp, fs * 0.2, null);
      if (h?.type === 'room') renamePrompt(h.rid);
    },
  } : undefined;

  const renamePrompt = (rid: string) => {
    const r = plan && findRoom(plan, rid);
    if (!r) return;
    setPrompt({ kind: 'text', title: 'Room name', value: r.name, apply: (v) => core.act((p) => renameRoom2(p, rid, v)) ?? undefined });
  };

  const cancelDrag = () => {
    const b = ctl.current.cancel();
    if (b) core.preview(b);
    before.current = null;
    setFrame(null);
    if (underlayDrag.current) { underlayDrag.current = false; U.cancel(); }
  };

  /** Arrow keys: the selected room, wall or door moves 1 in (Shift 1 ft), one undo step each. */
  const nudge = (dx: number, dy: number) => {
    if (!plan || !sel) return;
    const g: Grab = sel.type === 'room' ? { kind: 'room', rid: sel.rid } : sel.type === 'wall'
      ? { kind: 'wall', rid: sel.rid, vid: sel.vid, off: sel.off } : { kind: 'open', rid: sel.rid, oid: sel.oid };
    const w0 = sel.type === 'room' ? labelPt(findRoom(plan, sel.rid)!) : handlePt(plan, sel);
    if (!w0) return;
    const res = ctl.current.start(plan, g, w0, core.editable);
    if ('message' in res) { say(res.message); return; }
    ctl.current.move({ x: w0.x + dx, y: w0.y + dy }, { alt: true, pxPerFt: 20 });
    const e = ctl.current.end();
    if (e.kind === 'plan') core.commit(e.before, e.working, e.sel);
    else if (e.kind === 'none' && e.message) say(e.message);
  };

  // The editor's keys (W2.4). Undo/Redo also stay on the header buttons.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (prompt || menu) return; // the dialog and the menu own the keyboard
      const k = keyIntent(e);
      if (!k) return;
      if (k.kind === 'escape') {
        if (ctl.current.active || underlayDrag.current) cancelDrag();
        else if (tool) { setTool(null); setSplitLine(null); }
        else if (U.mode !== 'none') U.setMode('none');
        else setSel(null);
        e.preventDefault();
        return;
      }
      if (ctl.current.active) return;
      e.preventDefault();
      if (k.kind === 'undo') core.undo();
      else if (k.kind === 'redo') core.redo();
      else if (k.kind === 'copy' && sel?.type === 'room') menuCtx?.copy(sel.rid);
      else if (k.kind === 'paste' && opts.view) paste(clip && plan ? offsetPaste(plan, clip, opts.view) : { x: opts.view.x + opts.view.w / 2, y: opts.view.y + opts.view.h / 2 });
      else if (k.kind === 'rename' && sel) renamePrompt(sel.rid);
      else if (k.kind === 'nudge') nudge(k.dx, k.dy);
      else if (k.kind === 'delete' && sel && !modeOn()) {   // W6: Delete does nothing while a mode is on
        if (sel.type === 'open') core.act((p) => deleteOpening(p, sel.rid, sel.oid), { select: null });
        else if (sel.type === 'room') core.act((p) => deleteRoom(p, sel.rid), { select: null });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const cursor = frame ? (frame.sel?.type === 'room' ? 'move' : 'grabbing')
    : U.mode === 'move' ? 'move' : U.mode === 'calibrate' || tool ? 'crosshair'
      : hover?.type === 'room' ? 'move' : hover?.type === 'wall' ? wallCursor(plan, hover) : hover?.type === 'open' ? 'ew-resize' : undefined;

  return {
    editor, frame, hover, tool, setTool: (t: 'split' | null) => { setTool(t); setSplitLine(null); if (t) say('Click a wall of the room where the new line should go.'); },
    splitLine, menu, menuItems, menuTitle: menu && plan ? menuTitle(menu.target, plan) : '', closeMenu: () => setMenu(null),
    prompt, setPrompt, cursor, renamePrompt,
  };
}

/** Rename without the numbered-name chip rule. */
function renameRoom2(p: Plan, rid: string, name: string) {
  const r = findRoom(p, rid);
  if (!r) return { ok: false, message: 'That room is gone' };
  const v = name.trim();
  if (!v) return { ok: false, message: 'Type a name' };
  if (v.length > 80) return { ok: false, message: 'A room name can be at most 80 characters.' };
  r.name = v;
  return { ok: true };
}

function wallCursor(plan: Plan | null, s: Extract<Sel, { type: 'wall' }>): string {
  const r = plan && findRoom(plan, s.rid);
  const E = r && edgeOf(r, s.vid);
  if (!E) return 'pointer';
  return Math.abs(E.a.y - E.b.y) < 1e-6 ? 'ns-resize' : Math.abs(E.a.x - E.b.x) < 1e-6 ? 'ew-resize' : 'not-allowed';
}

/** Ctrl+V: the copy lands beside the original when it is in view, else in the middle of the view. */
function offsetPaste(plan: Plan, clip: Room, v: ViewBox): Pt {
  const [x0, y0, x1, y1] = bbox(clip.corners);
  const c = { x: (x0 + x1) / 2 + (x1 - x0) + 1, y: (y0 + y1) / 2 };
  const inView = c.x > v.x && c.x < v.x + v.w && c.y > v.y && c.y < v.y + v.h;
  void plan;
  return inView ? c : { x: v.x + v.w / 2, y: v.y + v.h / 2 };
}
